import { useState, useEffect, useMemo } from "react";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import { interventionsApi, type Intervention } from "@services/api/interventions";
import BaseModal from "@components/modals/BaseModal";
import Icon1 from "@assets/icons/Icon";

const CATEGORY_OPTIONS = [
  { value: "mecanique", label: "Mécanique" },
  { value: "carrosserie", label: "Carrosserie" },
  { value: "vitrage", label: "Vitrage" },
  { value: "pneumatique", label: "Pneumatique" },
];

const TYPE_OPTIONS = [
  { value: "entretien", label: "Entretien" },
  { value: "reparation", label: "Réparation" },
  { value: "diagnostic", label: "Diagnostic" },
  { value: "controle_technique", label: "Contrôle technique" },
];

const CATEGORY_LABELS: Record<string, string> = {
  mecanique: "Mécanique",
  carrosserie: "Carrosserie",
  vitrage: "Vitrage",
  pneumatique: "Pneumatique",
};

const TYPE_LABELS: Record<string, string> = {
  entretien: "Entretien",
  reparation: "Réparation",
  diagnostic: "Diagnostic",
  controle_technique: "Contrôle technique",
};

const TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  entretien: { bg: "#DBEAFE", color: "#1D4ED8" },
  reparation: { bg: "#FEE2E2", color: "#DC2626" },
  diagnostic: { bg: "#E0E7FF", color: "#4338CA" },
  controle_technique: { bg: "#D1FAE5", color: "#065F46" },
};

const DURATION_OPTIONS = [
  { value: "15", label: "15 min" },
  { value: "20", label: "20 min" },
  { value: "30", label: "30 min" },
  { value: "45", label: "45 min" },
  { value: "60", label: "1h" },
  { value: "90", label: "1h30" },
  { value: "120", label: "2h" },
  { value: "150", label: "2h30" },
  { value: "180", label: "3h" },
  { value: "240", label: "4h" },
  { value: "300", label: "5h" },
  { value: "360", label: "6h" },
  { value: "480", label: "8h" },
];

const MARGIN_OPTIONS = [
  { value: "0", label: "Aucune marge" },
  { value: "1", label: "1 jour" },
  { value: "2", label: "2 jours" },
  { value: "3", label: "3 jours" },
  { value: "5", label: "5 jours" },
  { value: "7", label: "1 semaine" },
  { value: "14", label: "2 semaines" },
];

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h${m.toString().padStart(2, "0")}` : `${h}h`;
}

// ─── Edit / Create Modal ─────────────────────────────────────

function InterventionModal({
  intervention,
  onClose,
  onSave,
  onDelete,
}: {
  intervention: Intervention | null; // null = create mode
  onClose: () => void;
  onSave: (data: { name: string; estimated_minutes: number; min_days_margin: number; category: string; intervention_type: string; active: boolean }) => void;
  onDelete?: () => void;
}) {
  const [name, setName] = useState(intervention?.name || "");
  const [minutes, setMinutes] = useState(String(intervention?.estimated_minutes || 60));
  const [margin, setMargin] = useState(String(intervention?.min_days_margin || 0));
  const [category, setCategory] = useState(intervention?.category || "mecanique");
  const [interventionType, setInterventionType] = useState(intervention?.intervention_type || "entretien");
  const [active, setActive] = useState(intervention?.active ?? true);

  const isCreate = !intervention;

  return (
    <BaseModal
      onClose={onClose}
      title={isCreate ? "Nouvelle intervention" : "Modifier l'intervention"}
      startIcon={<Icon1 name="sett-interventions" size={20} />}
      width="max-w-lg"
    >
      <div className="flex flex-col gap-4 p-4">
        <TextField
          label="Nom de l'intervention"
          value={name}
          onChange={(v: string) => setName(v)}
          placeholder="ex: Vidange + filtre huile"
        />

        <div className="flex flex-row gap-3">
          <div className="flex-1">
            <DropDown
              label="Catégorie"
              value={category}
              options={CATEGORY_OPTIONS}
              onSelect={(v) => setCategory(String(v))}
            />
          </div>
          <div className="flex-1">
            <DropDown
              label="Type"
              value={interventionType}
              options={TYPE_OPTIONS}
              onSelect={(v) => setInterventionType(String(v))}
            />
          </div>
        </div>

        <DropDown
          label="Durée estimée"
          value={minutes}
          options={DURATION_OPTIONS}
          onSelect={(v) => setMinutes(String(v))}
        />

        <DropDown
          label="Marge minimum pour restitution"
          value={margin}
          options={MARGIN_OPTIONS}
          onSelect={(v) => setMargin(String(v))}
        />

        <div className="text-h4 text-neutral-400">
          {parseInt(margin) > 0
            ? `Le client ne pourra pas choisir une date de restitution avant ${margin} jour${parseInt(margin) > 1 ? "s" : ""} après le dépôt pour cette intervention.`
            : "Aucune marge supplémentaire appliquée à la date de restitution."}
        </div>

        {!isCreate && (
          <label className="flex flex-row items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={active}
              onChange={() => setActive(!active)}
              className="w-4 h-4 accent-black"
            />
            <span className="text-h3 text-neutral-500">Active (visible dans le widget)</span>
          </label>
        )}

        <div className="flex flex-row items-center gap-2 pt-2">
          <Button
            label={isCreate ? "Créer" : "Enregistrer"}
            onClick={() => onSave({
              name: name.trim(),
              estimated_minutes: parseInt(minutes),
              min_days_margin: parseInt(margin),
              category,
              intervention_type: interventionType,
              active,
            })}
            disabled={!name.trim()}
            variant="small"
          />
          {!isCreate && onDelete && (
            <SecondaryButton
              label="Supprimer"
              onClick={onDelete}
              variant="small"
              danger
            />
          )}
        </div>
      </div>
    </BaseModal>
  );
}

// ─── Main Page ───────────────────────────────────────────────

export const Interventions = () => {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const canEdit = isOwnerOrAdmin(userData);

  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  const [editTarget, setEditTarget] = useState<Intervention | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchInterventions = async () => {
    if (!orgData?.id) return;
    try {
      const data = await interventionsApi.list(orgData.id);
      setInterventions(data);
    } catch {
      console.error("Failed to fetch interventions");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchInterventions();
  }, [orgData?.id]);

  const filtered = useMemo(() => {
    let list = interventions;
    if (filterType !== "all") {
      list = list.filter((i) => i.intervention_type === filterType);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((i) => i.name.toLowerCase().includes(q));
    }
    return list;
  }, [interventions, search, filterType]);

  // Group filtered interventions by type
  const grouped = useMemo(() => {
    const groups: Record<string, Intervention[]> = {};
    for (const i of filtered) {
      const type = i.intervention_type || "entretien";
      if (!groups[type]) groups[type] = [];
      groups[type].push(i);
    }
    return groups;
  }, [filtered]);

  const handleCreate = async (data: { name: string; estimated_minutes: number; min_days_margin: number; category: string; intervention_type: string; active: boolean }) => {
    if (!orgData?.id) return;
    try {
      const created = await interventionsApi.create({ ...data, org_id: orgData.id });
      setInterventions((prev) => [...prev, created]);
      setShowCreate(false);
      showNotification({ message: "Intervention créée", type: "success" });
    } catch {
      showNotification({ message: "Erreur lors de la création", type: "error" });
    }
  };

  const handleUpdate = async (data: { name: string; estimated_minutes: number; min_days_margin: number; category: string; intervention_type: string; active: boolean }) => {
    if (!editTarget) return;
    try {
      const updated = await interventionsApi.update(editTarget.id, data);
      setInterventions((prev) => prev.map((i) => (i.id === editTarget.id ? updated : i)));
      setEditTarget(null);
      showNotification({ message: "Intervention mise à jour", type: "success" });
    } catch {
      showNotification({ message: "Erreur lors de la mise à jour", type: "error" });
    }
  };

  const handleDelete = async () => {
    if (!editTarget) return;
    try {
      await interventionsApi.delete(editTarget.id);
      setInterventions((prev) => prev.filter((i) => i.id !== editTarget.id));
      setEditTarget(null);
      showNotification({ message: "Intervention supprimée", type: "success" });
    } catch {
      showNotification({ message: "Erreur lors de la suppression", type: "error" });
    }
  };

  // Type filter tabs
  const typeFilters = [
    { value: "all", label: "Tous" },
    ...TYPE_OPTIONS,
  ];

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10 w-full max-w-700">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Text1>Interventions</Text1>
          <Text2>Gérez les différentes interventions proposées.</Text2>
          <Text3>
            Configurez la liste des interventions par type. La durée estimée et la marge
            de restitution seront utilisées dans le widget de réservation.
          </Text3>
        </div>

        <div className="flex flex-col bg-white rounded-lg border-1 overflow-hidden">
          {/* Header */}
          <div className="flex flex-row items-center justify-between px-4 pt-4 pb-2">
            <span className="text-h3 text-neutral-400">
              Interventions ({interventions.length})
            </span>
          </div>

          {/* Type filter tabs */}
          <div className="flex flex-row items-center gap-1 px-4 pb-2 overflow-x-auto">
            {typeFilters.map((t) => {
              const isActive = filterType === t.value;
              const typeColor = t.value !== "all" ? TYPE_COLORS[t.value] : null;
              return (
                <button
                  key={t.value}
                  className="text-h4 font-medium px-3 py-1 rounded-full transition-colors whitespace-nowrap"
                  style={{
                    background: isActive
                      ? (typeColor?.bg || "#F5F5F5")
                      : "transparent",
                    color: isActive
                      ? (typeColor?.color || "#525252")
                      : "#A1A1A1",
                    border: isActive ? "none" : "1px solid transparent",
                  }}
                  onClick={() => setFilterType(t.value)}
                >
                  {t.label}
                  {t.value !== "all" && (() => {
                    const count = interventions.filter((i) => i.intervention_type === t.value).length;
                    return count > 0 ? ` (${count})` : "";
                  })()}
                </button>
              );
            })}
          </div>

          {/* Search + Create */}
          <div className="flex flex-row items-center gap-2 px-4 pb-3">
            <div className="flex-1">
              <TextField
                value={search}
                onChange={(v: string) => setSearch(v)}
                placeholder="Chercher .."
                noborder
              />
            </div>
            {canEdit && (
              <SecondaryButton
                label="Créer"
                variant="small"
                startIcon={<Icon1 name="plus" size={12} />}
                onClick={() => setShowCreate(true)}
              />
            )}
          </div>

          {/* List grouped by type */}
          {loading ? (
            <div className="px-4 py-8 text-center text-h3 text-neutral-300">
              Chargement...
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-h3 text-neutral-300">
              {search ? "Aucune intervention trouvée" : "Aucune intervention configurée"}
            </div>
          ) : (
            <div className="flex flex-col">
              {Object.entries(grouped).map(([type, items]) => {
                const typeColor = TYPE_COLORS[type] || TYPE_COLORS.entretien;
                return (
                  <div key={type}>
                    {/* Type group header (only show if not filtering by a single type) */}
                    {filterType === "all" && (
                      <div
                        className="flex flex-row items-center gap-2 px-4 py-2 border-t-1"
                        style={{ background: "#FAFAFA" }}
                      >
                        <span
                          className="text-h4 font-semibold px-2 py-0.5 rounded"
                          style={{ background: typeColor.bg, color: typeColor.color }}
                        >
                          {TYPE_LABELS[type] || type}
                        </span>
                        <span className="text-h4 text-neutral-400">
                          {items.length} intervention{items.length > 1 ? "s" : ""}
                        </span>
                      </div>
                    )}
                    {items.map((intervention) => (
                      <div
                        key={intervention.id}
                        className="flex flex-row items-center px-4 py-3 border-t-1 hover:bg-neutral-50 transition-colors"
                        style={{ gap: 12 }}
                      >
                        <span
                          className="text-h3 flex-1"
                          style={{
                            color: intervention.active ? "#525252" : "#A1A1A1",
                            fontFamily: "Poppins",
                            textDecoration: intervention.active ? "none" : "line-through",
                          }}
                        >
                          {intervention.name}
                        </span>
                        <span
                          className="text-h5 font-medium px-2 py-0.5 rounded"
                          style={{ background: typeColor.bg, color: typeColor.color, whiteSpace: "nowrap" }}
                        >
                          {CATEGORY_LABELS[intervention.category] || intervention.category}
                        </span>
                        {intervention.min_days_margin > 0 && (
                          <span
                            className="text-h4 font-medium px-2 py-0.5 rounded"
                            style={{ background: "#FEF3C7", color: "#B45309", whiteSpace: "nowrap" }}
                          >
                            +{intervention.min_days_margin}j
                          </span>
                        )}
                        <span
                          className="text-h3 text-neutral-400"
                          style={{ whiteSpace: "nowrap", minWidth: 60, textAlign: "right" }}
                        >
                          {formatDuration(intervention.estimated_minutes)}
                        </span>
                        {canEdit && (
                          <SecondaryButton
                            label="Gérer"
                            variant="small"
                            onClick={() => setEditTarget(intervention)}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreate && (
        <InterventionModal
          intervention={null}
          onClose={() => setShowCreate(false)}
          onSave={handleCreate}
        />
      )}
      {editTarget && (
        <InterventionModal
          intervention={editTarget}
          onClose={() => setEditTarget(null)}
          onSave={handleUpdate}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};
